import axios from 'axios';
import {
  getToken,
  setToken,
  clearToken,
  setInitialized,
  setInitializationPromise,
  logout,
} from '../../util/tokenManager';

const api = axios.create({
  baseURL: 'https://localhost:5002/api/v1',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue = [];
let isLoggingOut = false; // Flag to prevent refresh attempts during logout

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Initialize token on app start
export const initializeToken = async () => {
  const initPromise = (async () => {
    try {
      // Shorter timeout for faster initialization
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Token refresh timeout')), 2000)
      );

      // Create a custom request that won't log 401 errors
      const refreshPromise = api.post(
        '/users/token/refresh',
        {},
        {
          // Custom config to suppress error logging
          validateStatus: function (status) {
            // Don't throw for 401 - handle it as a normal response
            return status < 500;
          },
        }
      );

      const response = await Promise.race([refreshPromise, timeoutPromise]);

      // Check if the response was successful
      if (response.status === 200 && response.data.accessToken) {
        const newToken = response.data.accessToken;
        setToken(newToken);
        setInitialized(true);
        return newToken;
      } else {
        // 401 or other non-success status
        setInitialized(true);
        return null;
      }
    } catch (error) {
      // Silently handle initialization failures in production
      setInitialized(true);
      return null;
    }
  })();

  // Store the promise so other components can wait for it
  setInitializationPromise(initPromise);

  return initPromise;
};

// Logout function
export const logoutUser = async () => {
  // Set flag to prevent token refresh attempts during logout
  isLoggingOut = true;

  // Note: Local state cleanup (token, user data) and navigation are handled by useLogout hook
  // This function focuses only on backend cleanup

  try {
    // Create a timeout promise (3 seconds max wait)
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Logout timeout')), 3000)
    );

    // Race between logout API call and timeout
    await Promise.race([
      api.post('/users/logout'),
      timeoutPromise
    ]);

    // Backend call succeeded - refresh token cookie cleared
  } catch (error) {
    // Either timeout or API error - continue anyway
    console.log('Logout API call failed or timed out:', error.message);
    // Local state is already cleared, so user is logged out on frontend
  } finally {
    // Reset the flag
    isLoggingOut = false;
    // Navigation is now handled by useLogout hook using React Router
  }
};

// Request interceptor to add token to headers
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Don't block requests if no token - let them proceed without auth
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token-related errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't intercept 401s for login/auth endpoints - let them pass through
    const isAuthEndpoint =
      originalRequest.url?.includes('/login') ||
      originalRequest.url?.includes('/signup') ||
      originalRequest.url?.includes('/auth/');

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      // Skip token refresh if we're in the middle of logging out
      if (isLoggingOut) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the token using the httpOnly cookie
        // Suppress 401 logs for this request too
        const response = await api.post(
          '/users/token/refresh',
          {},
          {
            validateStatus: function (status) {
              return status < 500;
            },
          }
        );

        // Check if refresh was successful
        if (response.status === 200 && response.data.accessToken) {
          const newToken = response.data.accessToken;
          setToken(newToken);
          processQueue(null, newToken);

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          // Token refresh failed (401)
          throw new Error('Token refresh failed');
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearToken();
        window.location.href = '/swapandtop/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
